/**
 * Generic file uploader — sub-spec 07 Plan 3 M5.
 *
 * Drives the three-step upload dance:
 *   1. POST to a presign endpoint → { fileId, uploadUrl, requiredHeaders }
 *   2. PUT bytes directly to R2 via the presigned URL
 *   3. POST to a confirm endpoint → server verifies bytes landed + persists
 *
 * Caller supplies the presign + confirm URLs (so a single component can
 * back vaccination docs, belongings photos, generic /api/files uploads,
 * and the per-entity branding endpoints).
 *
 * On success, calls `onUploaded(fileId)`. On error, calls `onError(message)`
 * AND surfaces the message inline. Disabled while uploading.
 */
import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';

export interface FileUploaderProps {
  /**
   * URL of the presign endpoint. POSTed with the body
   * `{ contentType, contentLength, originalFileName, entityType?, entityId? }`.
   * Returns `{ fileId, uploadUrl, requiredHeaders }`. The optional
   * `entityType` + `entityId` fields are forwarded only when set — generic
   * /api/files presign needs them; per-entity endpoints (branding,
   * vaccinations, belongings) infer them server-side.
   */
  presignUrl: string;
  /**
   * URL of the confirm endpoint. POSTed with `{ fileId }` after the PUT.
   */
  confirmUrl: string;
  /**
   * Optional entity context forwarded to the presign endpoint for generic
   * /api/files uploads. Branding / vaccinations / belongings infer these
   * server-side and ignore the fields.
   */
  entityType?: string;
  entityId?: string;
  /** Accept attribute on the file input, e.g. "image/png,image/svg+xml". */
  accept?: string;
  /** Label rendered next to the upload icon. Defaults to "Upload file". */
  label?: string;
  /** Disables the picker (e.g. when parent is mid-save). */
  disabled?: boolean;
  /** Called with the newly-uploaded file id on success. */
  onUploaded: (fileId: string) => void;
  /** Called with the error message on failure. Optional — defaults to a no-op. */
  onError?: (message: string) => void;
}

interface PresignResponse {
  fileId: string;
  uploadUrl: string;
  requiredHeaders?: Record<string, string>;
}

export function FileUploader({
  presignUrl,
  confirmUrl,
  entityType,
  entityId,
  accept,
  label = 'Upload file',
  disabled = false,
  onUploaded,
  onError,
}: FileUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<'idle' | 'uploading' | 'confirming'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    setStatus('uploading');
    try {
      // 1. Presign.
      const presignBody: Record<string, unknown> = {
        contentType: file.type || 'application/octet-stream',
        contentLength: file.size,
        originalFileName: file.name,
      };
      if (entityType) presignBody.entityType = entityType;
      if (entityId) presignBody.entityId = entityId;
      const presignRes = await fetch(presignUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(presignBody),
      });
      if (!presignRes.ok) {
        const body = (await presignRes.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `presign failed (${presignRes.status})`);
      }
      const presign = (await presignRes.json()) as PresignResponse;

      // 2. PUT bytes directly to R2.
      const putHeaders: Record<string, string> = {};
      for (const [k, v] of Object.entries(presign.requiredHeaders ?? {})) {
        putHeaders[k] = v;
      }
      const putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: putHeaders,
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`upload failed (${putRes.status})`);
      }

      // 3. Confirm.
      setStatus('confirming');
      const confirmRes = await fetch(confirmUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId: presign.fileId }),
      });
      if (!confirmRes.ok) {
        const body = (await confirmRes.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `confirm failed (${confirmRes.status})`);
      }

      onUploaded(presign.fileId);
    } catch (e) {
      const message = (e as Error).message ?? 'Upload failed';
      setError(message);
      onError?.(message);
    } finally {
      setStatus('idle');
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const busy = status !== 'idle';
  return (
    <div>
      <label
        className={`inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm ${
          busy || disabled ? 'opacity-50 pointer-events-none' : 'hover:bg-secondary'
        }`}
      >
        <Upload size={14} />
        <span>{busy ? (status === 'uploading' ? 'Uploading…' : 'Confirming…') : label}</span>
        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          accept={accept}
          disabled={busy || disabled}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
      </label>
      {error && (
        <p role="alert" className="mt-1.5 text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
